import {
  type GcpRateLimiterId,
  type RateLimiterSettings,
  type RegionalGcpRateLimiterId,
  gcpRateLimiterIds,
} from '../types';
import {
  baseVendorApiSettingsDefaults,
  defaultVendorApiHttpAdvancedSettings,
  getDefaultRateLimitationSettings,
} from './base';
import type { BaseHttpBasedVendorApiSettingsWithMultipleRateLimitersDefaults } from './types';

const rateLimitationDefaults =
  getDefaultRateLimitationSettings<GcpRateLimiterId>(gcpRateLimiterIds);

export const gcpDefaults: BaseHttpBasedVendorApiSettingsWithMultipleRateLimitersDefaults<GcpRateLimiterId> & {
  scopes: string[];
  regionalRateLimiters: {
    [id in RegionalGcpRateLimiterId]: Required<RateLimiterSettings>;
  };
} = {
  ...baseVendorApiSettingsDefaults,
  ...rateLimitationDefaults,
  ...defaultVendorApiHttpAdvancedSettings,
  maxConcurrentRequests: 200,
  scopes: [
    'https://www.googleapis.com/auth/cloud-platform',
    'https://www.googleapis.com/auth/compute',
  ],
  rateLimiters: {
    // default values should follow https://cloud.google.com/compute/api-quota
    ...rateLimitationDefaults.rateLimiters,
    'compute.googleapis.com/default': {
      maxCapacity: 1500,
      refillRate: 1500,
      refillRateIntervalMs: 60 * 1000,
    },
    'compute.googleapis.com/read_requests': {
      maxCapacity: 1500,
      refillRate: 1500,
      refillRateIntervalMs: 60 * 1000,
    },
    'compute.googleapis.com/list_requests': {
      maxCapacity: 1500,
      refillRate: 1500,
      refillRateIntervalMs: 60 * 1000,
    },
    'compute.googleapis.com/operation_read_requests': {
      maxCapacity: 1500,
      refillRate: 1500,
      refillRateIntervalMs: 60 * 1000,
    },
    'compute.googleapis.com/heavy_weight_read_requests': {
      maxCapacity: 750,
      refillRate: 750,
      refillRateIntervalMs: 60 * 1000,
    },
    'cloudasset.googleapis.com/search_requests': {
      maxCapacity: 200,
      refillRate: 200,
      refillRateIntervalMs: 60 * 1000,
    }, //  https://cloud.google.com/asset-inventory/docs/quota
    default: {
      maxCapacity: 750,
      refillRate: 750,
      refillRateIntervalMs: 60 * 1000,
    },
  },
  regionalRateLimiters: {
    'compute.googleapis.com/default_per_region': {
      maxCapacity: 1500,
      refillRate: 1500,
      refillRateIntervalMs: 60 * 1000,
    },
    'compute.googleapis.com/read_requests_per_region': {
      maxCapacity: 1500,
      refillRate: 1500,
      refillRateIntervalMs: 60 * 1000,
    },
    'compute.googleapis.com/list_requests_per_region': {
      maxCapacity: 1500,
      refillRate: 1500,
      refillRateIntervalMs: 60 * 1000,
    },
    'compute.googleapis.com/operation_read_requests_per_region': {
      maxCapacity: 1500,
      refillRate: 1500,
      refillRateIntervalMs: 60 * 1000,
    },
    'compute.googleapis.com/get_serial_port_output_requests_per_region': {
      maxCapacity: 1500,
      refillRate: 1500,
      refillRateIntervalMs: 60 * 1000,
    },
    'compute.googleapis.com/heavy_weight_read_requests_per_region': {
      maxCapacity: 750,
      refillRate: 750,
      refillRateIntervalMs: 60 * 1000,
    },
    'compute.googleapis.com/instance_list_referrers_requests_per_region': {
      maxCapacity: 3000,
      refillRate: 3000,
      refillRateIntervalMs: 60 * 1000,
    },
    'compute.googleapis.com/network_endpoint_list_requests_per_region': {
      maxCapacity: 1500,
      refillRate: 1500,
      refillRateIntervalMs: 60 * 1000,
    },
    'compute.googleapis.com/regional_network_endpoint_list_requests_per_region':
      {
        maxCapacity: 1500,
        refillRate: 1500,
        refillRateIntervalMs: 60 * 1000,
      },
  },
};
